import { useState, useEffect } from 'react';
import {
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  CarFront,
} from 'lucide-react';
import { ExportCSVButton, ExportPDFButton } from '../components/ExportButtons';
import { getBookingsReport, listAllGolfCourses, listSettings } from '../api';
import ReportFilterBar from '../components/ReportFilterBar';
import { fmtReportDate, exportCSV, fmtCSVDate, fmtCSVFilename } from '../utils/reportExport';
import PrintBanner from '../components/PrintBanner/PrintBanner';
import styles from './BookingsReportPage.module.css';
import HolePill from '../components/HolePill';
import PlayerTypePill from '../components/PlayerTypePill';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function addDaysISO(iso, n) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().split('T')[0];
}

function formatLongDate(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });
}

function formatTime(timeStr) {
  if (!timeStr) return '—';
  // Accepts "HH:MM" or ISO datetime
  const match = timeStr.match(/(\d{1,2}):(\d{2})/);
  if (!match) return timeStr;
  let h = parseInt(match[1], 10);
  const m = match[2];
  const ap = h < 12 ? 'AM' : 'PM';
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${m} ${ap}`;
}

// Build a sorted list of ISO dates between from and to inclusive
function dateRange(from, to) {
  const dates = [];
  let cur = from;
  while (cur <= to) {
    dates.push(cur);
    cur = addDaysISO(cur, 1);
  }
  return dates;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function PlayerRow({ player, ttStartTime, ttHole, nineHolePace }) {
  const isMember = player.is_member;

  let crossoverCell = '—';
  if (player.holes_to_play === 18 && ttStartTime && ttHole != null && nineHolePace) {
    const [, timePart] = ttStartTime.split('T');
    const [h, m] = timePart.split(':').map(Number);
    const totalMinutes = h * 60 + m + nineHolePace;
    const crossoverH = Math.floor(totalMinutes / 60) % 24;
    const crossoverM = totalMinutes % 60;
    const period = crossoverH >= 12 ? 'PM' : 'AM';
    const h12 = crossoverH % 12 || 12;
    const timeStr = `${h12}:${String(crossoverM).padStart(2, '0')} ${period}`;
    const crossoverHole = ttHole === 1 ? 10 : 1;
    crossoverCell = `~${timeStr} · Hole ${crossoverHole}`;
  }

  return (
    <tr className={styles.playerRow}>
      <td className={styles.playerName}>{player.name || '—'}</td>
      <td className={styles.playerMemberNum}>{player.member_number || '—'}</td>
      <td>
        <PlayerTypePill isMember={isMember} />
      </td>
      <td>
        {player.needs_golf_cart ? (
          <span className={styles.cartYes}>
            <CarFront size={12} strokeWidth={1.5} />
            Yes
          </span>
        ) : (
          <span className={styles.cartNo}>No</span>
        )}
      </td>
      <td className={styles.playerHoles}>{player.holes_to_play ? `${player.holes_to_play} holes` : '—'}</td>
      <td className={styles.playerCrossover}>{crossoverCell}</td>
    </tr>
  );
}

function BookingRow({ booking, defaultOpen, ttStartTime, ttHole, nineHolePace }) {
  const [open, setOpen] = useState(defaultOpen);

  useEffect(() => {
    setOpen(defaultOpen);
  }, [defaultOpen]);

  const members = booking.member_count || 0;
  const guests  = booking.guest_count  || 0;
  const carts   = booking.cart_count   || 0;

  return (
    <div className={styles.bookingRow}>
      <div
        className={styles.bookingHeader}
        onClick={() => setOpen(v => !v)}
        role="button"
        aria-expanded={open}
      >
        <span className={styles.bookingNum}>{booking.booking_number}</span>
        <span className={styles.bookingBy}>{booking.booked_by || '—'}</span>
        <div className={styles.bookingStats}>
          {members > 0 && (
            <span className={styles.chipMember}>{members} member{members !== 1 ? 's' : ''}</span>
          )}
          {guests > 0 && (
            <span className={styles.chipGuest}>{guests} guest{guests !== 1 ? 's' : ''}</span>
          )}
          {carts > 0 && (
            <span className={styles.bookingCartStat}>
              <CarFront size={11} strokeWidth={1.5} />
              {carts} cart{carts !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <ChevronDown
          size={14}
          strokeWidth={1.5}
          className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`}
        />
      </div>
      {open && (
        <div className={styles.playersWrap}>
          <table className={styles.playersTable}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Member number</th>
                <th>Type</th>
                <th>Needs cart?</th>
                <th>Holes to play</th>
                <th>Crossover</th>
              </tr>
            </thead>
            <tbody>
              {(booking.players || []).map((p, i) => (
                <PlayerRow key={i} player={p} ttStartTime={ttStartTime} ttHole={ttHole} nineHolePace={nineHolePace} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TeeTimeGroup({ ttGroup, expandAll, nineHolePace }) {
  const totalPlayers  = ttGroup.bookings.reduce((s, b) => s + (b.player_count || 0), 0);
  const totalMembers  = ttGroup.bookings.reduce((s, b) => s + (b.member_count || 0), 0);
  const totalCarts    = ttGroup.bookings.reduce((s, b) => s + (b.cart_count || 0), 0);

  return (
    <div className={styles.ttGroup}>
      <div className={styles.ttHeader}>
        <span className={styles.ttTime}>{formatTime(ttGroup.start_time)}</span>
        <span className={styles.ttCourse}>{ttGroup.golf_course}</span>
        <HolePill hole={ttGroup.hole} />
        <div className={styles.ttStats}>
          <span className={styles.ttStat}><strong>{ttGroup.bookings.length}</strong> booking{ttGroup.bookings.length !== 1 ? 's' : ''}</span>
          <span className={styles.ttStat}><strong>{totalPlayers}</strong> player{totalPlayers !== 1 ? 's' : ''}</span>
          {totalMembers > 0 && (
            <span className={styles.chipMember}>{totalMembers} member{totalMembers !== 1 ? 's' : ''}</span>
          )}
          {(totalPlayers - totalMembers) > 0 && (
            <span className={styles.chipGuest}>{totalPlayers - totalMembers} guest{(totalPlayers - totalMembers) !== 1 ? 's' : ''}</span>
          )}
          {totalCarts > 0 && (
            <span className={styles.ttStatCarts}>
              <CarFront size={11} strokeWidth={1.5} />
              <strong>{totalCarts}</strong> cart{totalCarts !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
      {ttGroup.bookings.map((bk, i) => (
        <BookingRow key={bk.booking_number || i} booking={bk} defaultOpen={expandAll} ttStartTime={ttGroup.start_time} ttHole={ttGroup.hole} nineHolePace={nineHolePace} />
      ))}
      {/* Crossover arrivals section */}
      {ttGroup.crossover_arrivals && ttGroup.crossover_arrivals.length > 0 && (
        <CrossoverSection arrivals={ttGroup.crossover_arrivals} expandAll={false} />
      )}
    </div>
  );
}

function CrossoverSection({ arrivals, expandAll }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (expandAll) setOpen(true);
  }, [expandAll]);

  return (
    <div className={styles.crossoverSection}>
      <div
        className={styles.crossoverHeader}
        onClick={() => setOpen(v => !v)}
        role="button"
        aria-expanded={open}
      >
        <ChevronDown
          size={13}
          strokeWidth={1.5}
          className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`}
        />
        <span>Crossover arrivals ({arrivals.length})</span>
      </div>
      {open && (
        <div className={styles.playersWrap}>
          <table className={styles.playersTable}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Member number</th>
                <th>Type</th>
                <th>Needs cart?</th>
                <th>Holes to play</th>
                <th>Crossover</th>
              </tr>
            </thead>
            <tbody>
              {arrivals.map((p, i) => (
                <PlayerRow key={i} player={p} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function computeSummary(teeTimes) {
  if (!teeTimes) return null;
  const uniqueMemberNums = new Set(
    teeTimes.flatMap(tt =>
      tt.bookings.flatMap(b =>
        (b.players || [])
          .filter(p => p.is_member && p.member_number)
          .map(p => p.member_number)
      )
    )
  );
  return {
    tee_times:       teeTimes.length,
    bookings:        teeTimes.reduce((s, tt) => s + tt.bookings.length, 0),
    players:         teeTimes.reduce((s, tt) => s + (tt.total_players || 0), 0),
    members:         teeTimes.reduce((s, tt) => s + tt.bookings.reduce((bs, b) => bs + (b.member_count || 0), 0), 0),
    unique_members:  uniqueMemberNums.size,
    guests:          teeTimes.reduce((s, tt) => s + tt.bookings.reduce((bs, b) => bs + (b.guest_count || 0), 0), 0),
    carts:           teeTimes.reduce((s, tt) => s + (tt.total_cart_count || 0), 0),
  };
}

function computeBreakdown(teeTimes) {
  if (!teeTimes || teeTimes.length === 0) return [];
  const groups = new Map();
  for (const tt of teeTimes) {
    const key = `${tt.golf_course}|||${tt.hole}`;
    if (!groups.has(key)) {
      groups.set(key, { golf_course: tt.golf_course, hole: tt.hole, tee_times_list: [] });
    }
    groups.get(key).tee_times_list.push(tt);
  }
  return Array.from(groups.values())
    .sort((a, b) => {
      if (a.golf_course < b.golf_course) return -1;
      if (a.golf_course > b.golf_course) return 1;
      return a.hole - b.hole;
    })
    .map(({ golf_course, hole, tee_times_list }) => {
      const uniqueMemberNums = new Set(
        tee_times_list.flatMap(tt =>
          tt.bookings.flatMap(b =>
            (b.players || [])
              .filter(p => p.is_member && p.member_number)
              .map(p => p.member_number)
          )
        )
      );
      return {
        golf_course,
        hole,
        tee_times:      tee_times_list.length,
        bookings:       tee_times_list.reduce((s, tt) => s + tt.bookings.length, 0),
        players:        tee_times_list.reduce((s, tt) => s + (tt.total_players || 0), 0),
        members:        tee_times_list.reduce((s, tt) => s + tt.bookings.reduce((bs, b) => bs + (b.member_count || 0), 0), 0),
        unique_members: uniqueMemberNums.size,
        guests:         tee_times_list.reduce((s, tt) => s + tt.bookings.reduce((bs, b) => bs + (b.guest_count || 0), 0), 0),
        carts:          tee_times_list.reduce((s, tt) => s + (tt.total_cart_count || 0), 0),
      };
    });
}

function DaySummaryBar({ summary, teeTimes, expandAll }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (expandAll) setOpen(true);
    else setOpen(false);
  }, [expandAll]);

  const breakdown = teeTimes ? computeBreakdown(teeTimes) : [];
  const showToggle = breakdown.length > 1;

  if (!summary) return null;
  return (
    <div className={styles.summaryBar}>
      {/* Always-visible totals row */}
      <div className={styles.summaryBarRow}>
        <span className={styles.summaryStat}><strong>{summary.tee_times ?? 0}</strong> tee time{summary.tee_times !== 1 ? 's' : ''}</span>
        <span className={styles.summarySep} />
        <span className={styles.summaryStat}><strong>{summary.bookings ?? 0}</strong> booking{summary.bookings !== 1 ? 's' : ''}</span>
        <span className={styles.summarySep} />
        <span className={styles.summaryStat}><strong>{summary.players ?? 0}</strong> players</span>
        <span className={styles.summarySep} />
        <span className={styles.summaryStat}><strong>{summary.members ?? 0}</strong> member{summary.members !== 1 ? 's' : ''} · <strong>{summary.unique_members ?? 0}</strong> unique members · <strong>{summary.guests ?? 0}</strong> guest{summary.guests !== 1 ? 's' : ''}</span>
        <span className={styles.summarySep} />
        <span className={styles.summaryStat}>
          <CarFront size={12} strokeWidth={1.5} />
          <strong>{summary.carts ?? 0}</strong> carts needed
        </span>
        {showToggle && (
          <button
            className={styles.summaryToggle}
            onClick={() => setOpen(v => !v)}
            aria-expanded={open}
            aria-label={open ? 'Collapse course breakdown' : 'Expand course breakdown'}
          >
            {open ? <ChevronUp size={12} strokeWidth={1.5} /> : <ChevronDown size={12} strokeWidth={1.5} />}
            breakdown
          </button>
        )}
      </div>

      {/* Collapsible breakdown table */}
      {showToggle && (
        <div className={`${styles.summaryBreakdown}${open || expandAll ? '' : ` ${styles.summaryBreakdownHidden}`}`}>
          <table className={styles.breakdownTable}>
            <thead>
              <tr>
                <th>Course</th>
                <th>Hole</th>
                <th className={styles.bdNum}>Tee times</th>
                <th className={styles.bdNum}>Bookings</th>
                <th className={styles.bdNum}>Players</th>
                <th className={styles.bdNum}>Members</th>
                <th className={styles.bdNum}>Unique members</th>
                <th className={styles.bdNum}>Guests</th>
                <th className={styles.bdNum}>Carts</th>
              </tr>
            </thead>
            <tbody>
              {breakdown.map((row, i) => (
                <tr key={i}>
                  <td>{row.golf_course}</td>
                  <td>
                    <HolePill hole={row.hole} />
                  </td>
                  <td className={styles.bdNum}>{row.tee_times}</td>
                  <td className={styles.bdNum}>{row.bookings}</td>
                  <td className={styles.bdNum}>{row.players}</td>
                  <td className={styles.bdNum}>{row.members}</td>
                  <td className={styles.bdNum}>{row.unique_members}</td>
                  <td className={styles.bdNum}>{row.guests}</td>
                  <td className={styles.bdNum}>{row.carts}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function BookingsReportPage() {
  const today = todayISO();

  const [dateFrom, setDateFrom]     = useState(today);
  const [dateTo, setDateTo]         = useState(addDaysISO(today, 6));
  const [courseId, setCourseId]     = useState('');
  const [hole, setHole]             = useState('');
  const [courses, setCourses]       = useState([]);

  // Applied range — the list of dates we're navigating
  const [dates, setDates]           = useState([]);
  const [dayIdx, setDayIdx]         = useState(0);
  const [expandAll, setExpandAll]   = useState(false);

  // All tee times returned by the single range fetch
  const [allTeeTimes, setAllTeeTimes] = useState([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);

  const [companyInfo, setCompanyInfo] = useState({ name: '', address: '', website: '', logo: '' });
  const [nineHolePace, setNineHolePace] = useState(60);

  useEffect(() => {
    listAllGolfCourses()
      .then(r => setCourses(r.items || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    listSettings()
      .then(data => {
        const map = {};
        (data.settings || data.items || []).forEach(s => { map[s.key] = s.value; });
        setCompanyInfo({ name: map.company_name || '', address: map.company_address || '', website: map.company_website || '', logo: map.company_logo || '' });
        const pace = parseInt(map.nine_hole_pace || '60', 10);
        setNineHolePace(isNaN(pace) ? 60 : pace);
      })
      .catch(() => {});
  }, []);

  const currentDate = dates[dayIdx] || null;
  const dayTeeTimes = currentDate
    ? allTeeTimes.filter(tt => tt.date === currentDate)
    : [];

  const handleApply = () => {
    if (!dateFrom || !dateTo || dateFrom > dateTo) return;
    const list = dateRange(dateFrom, dateTo);
    setDates(list);
    setDayIdx(0);
    setExpandAll(false);
    setAllTeeTimes([]);
    setLoading(true);
    setError(null);
    getBookingsReport({ dateFrom, dateTo, courseId: courseId || undefined, hole: hole || undefined })
      .then(data => {
        setAllTeeTimes(data.tee_times || []);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  };

  const handlePrint = () => {
    if (!dates.length) return;
    window.print();
  };

  const handleExportCSV = () => {
    if (!allTeeTimes.length) return;
    const headers = ['Date', 'Time', 'Course', 'Hole', 'Booking #', 'Players', 'Members', 'Guests', 'Carts'];
    const rows = [];
    for (const tt of allTeeTimes) {
      const dateLabel = fmtCSVDate(tt.date);
      const timePart = tt.start_time ? tt.start_time.split('T')[1] : null;
      const timeLabel = timePart ? formatTime(timePart) : '—';
      const holeLabel = `Hole ${tt.hole}`;
      for (const bk of tt.bookings) {
        rows.push([
          dateLabel, timeLabel, tt.golf_course, holeLabel,
          String(bk.booking_number ?? ''),
          String(bk.player_count ?? 0),
          String(bk.member_count ?? 0),
          String(bk.guest_count ?? 0),
          String(bk.cart_count ?? 0),
        ].map(v => (v.includes(',') ? `"${v}"` : v)));
      }
    }
    const filename = fmtCSVFilename('bookings', dates[0], dates[dates.length - 1]);
    exportCSV(headers, rows, filename);
  };

  return (
    <div className={styles.page}>
      {/* Toolbar — hidden when printing */}
      <div className="no-print">
        <ReportFilterBar
          title="Bookings"
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateFromChange={setDateFrom}
          onDateToChange={setDateTo}
          dateFromLabel="Start"
          dateToLabel="End"
          courseId={courseId}
          courses={courses}
          onCourseChange={setCourseId}
          onApply={handleApply}
          extraFilters={
            <>
              <label className={styles.label}>Hole:</label>
              <select
                className={styles.select}
                value={hole}
                onChange={e => setHole(e.target.value)}
              >
                <option value="">All holes</option>
                <option value="1">Hole 1</option>
                <option value="10">Hole 10</option>
              </select>
            </>
          }
        >
          {allTeeTimes.length > 0 && (
            <>
              <ExportCSVButton onClick={handleExportCSV} />
              <ExportPDFButton onClick={handlePrint} />
            </>
          )}
        </ReportFilterBar>
      </div>

      {/* Day navigation strip — hidden when printing */}
      {dates.length > 0 && (
        <div className={`${styles.dayNav} no-print`}>
          <div className={styles.dayNavCenter}>
            <button
              className={styles.navBtn}
              onClick={() => { setDayIdx(i => i - 1); setExpandAll(false); }}
              disabled={dayIdx === 0}
              aria-label="Previous day"
            >
              <ChevronLeft size={16} strokeWidth={1.5} />
            </button>
            <div>
              <div className={styles.dayNavDate}>{currentDate ? formatLongDate(currentDate) : '—'}</div>
              <div className={styles.dayNavCounter}>Day {dayIdx + 1} of {dates.length}</div>
            </div>
            <button
              className={styles.navBtn}
              onClick={() => { setDayIdx(i => i + 1); setExpandAll(false); }}
              disabled={dayIdx === dates.length - 1}
              aria-label="Next day"
            >
              <ChevronRight size={16} strokeWidth={1.5} />
            </button>
          </div>
          <div className={styles.dayNavRight}>
            <button
              className={styles.btnSecondary}
              onClick={() => setExpandAll(v => !v)}
            >
              <ChevronDown size={12} strokeWidth={1.5} />
              {expandAll ? 'Collapse all' : 'Expand all'}
            </button>
          </div>
        </div>
      )}

      {/* Body */}
      <div className={styles.body}>
        <PrintBanner
          companyInfo={companyInfo}
          reportTitle="Bookings Report"
          dateRange={dates.length ? `${fmtReportDate(dates[0])} – ${fmtReportDate(dates[dates.length - 1])}` : undefined}
          course={courseId ? (courses.find(c => String(c.id) === String(courseId))?.name || 'All courses') : 'All courses'}
          generatedOn={new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        />

        {/* Screen-only: states + current-day view */}
        <div className="no-print">
          {dates.length === 0 && (
            <div className={styles.emptyState}>
              Set a date range and click Apply to load the bookings report.
            </div>
          )}
          {dates.length > 0 && loading && (
            <div className={styles.emptyState}>Loading...</div>
          )}
          {dates.length > 0 && error && !loading && (
            <div className={styles.errorState}>Failed to load data. Please try again.</div>
          )}
          {dates.length > 0 && !loading && !error && (
            <>
              <DaySummaryBar summary={computeSummary(dayTeeTimes)} teeTimes={dayTeeTimes} expandAll={expandAll} />
              {dayTeeTimes.length === 0 ? (
                <div className={styles.emptyState}>No bookings on this day for the selected filters.</div>
              ) : (
                dayTeeTimes.map((tt, i) => (
                  <TeeTimeGroup key={`${tt.start_time}-${tt.hole}-${i}`} ttGroup={tt} expandAll={expandAll} nineHolePace={nineHolePace} />
                ))
              )}
            </>
          )}
        </div>

        {/* Print: all days */}
        {dates.length > 0 && (
          <div className={styles.printAllDays}>
            {dates.map((iso, di) => {
              const dayTTs = allTeeTimes.filter(tt => tt.date === iso);
              return (
                <div key={iso} className={styles.printDay}>
                  <div className={styles.printDayHeader}>{formatLongDate(iso)}</div>
                  <DaySummaryBar summary={computeSummary(dayTTs)} teeTimes={dayTTs} />
                  {dayTTs.map((tt, ti) => (
                    <TeeTimeGroup key={`${iso}-${tt.start_time}-${tt.hole}-${ti}`} ttGroup={tt} expandAll nineHolePace={nineHolePace} />
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
